import React, { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../../../contexts/AuthContext";
import { useSidebarLayout } from "../../../../../../contexts/SidebarLayoutContext";
import { ESTIMATE_STATUS_OPTIONS } from "../../../../../../constants/estimateStatus";
import {
  PAGE_H,
  PAGE_W,
  toBase64FromPublic,
} from "../../../../../../utils/PDFUtils";
import { getBackendUrl } from "../../../../../../utils/env";
import ConfirmDeleteModal from "../../../../components/ConfirmDeleteModal";
import EstimatePopupForm from "./EstimatePopupForm";
import {
  buildQuotationBodyHTML,
  buildQuotationPrintHTML,
} from "./quotationPdf";

const EstimatePopupContainer = ({
  isOpen,
  onClose,
  data,
  onUpdate,
  loading,
  getAdjustmentHistory,
  getPendingAdjustment,
  updatePendingAdjustment,
  deletePendingAdjustment,
  deleteAdjustmentHistory,
  onAdjustmentDeleted,
  profileInfo,
}) => {
  const { token } = useContext(AuthContext);
  const { hasPermission, permissions } = useSidebarLayout();

  const [form, setForm] = useState({
    unitPrice: "",
    quantity: "",
    vatPercent: 0,
    status: null,
  });

  const [editableTiers, setEditableTiers] = useState([]);
  const [originalTiers, setOriginalTiers] = useState([]);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [pendingAdjustment, setPendingAdjustment] = useState(null);
  const [pendingAdjustmentFromApi, setPendingAdjustmentFromApi] = useState(null);
  const [originalPendingAdjustmentFromApi, setOriginalPendingAdjustmentFromApi] = useState(null);

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [historyDeleteIds, setHistoryDeleteIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const canEditEstimateStatus = hasPermission("chinh_sua_trang_thai_bao_phi");

  useEffect(() => {
    if (!isOpen) return;

    const permissionSlugs = (permissions || []).flatMap((group) =>
      (group.permissions || []).map((permission) => permission.nameSlug),
    );

    console.log("[EstimatePopup] hasPermission('chinh_sua_trang_thai_bao_phi'):", canEditEstimateStatus);
    console.log("[EstimatePopup] current permission slugs:", permissionSlugs);
    console.log("[EstimatePopup] full permission groups:", permissions || []);
  }, [isOpen, canEditEstimateStatus, permissions]);

  useEffect(() => {
    if (data) {
      setForm({
        unitPrice: data.unitPrice ?? "",
        quantity: data.quantity ?? 1,
        vatPercent: data.vatPercent ?? 0,
        status: ESTIMATE_STATUS_OPTIONS.find((opt) => opt?.value === data?.status) || null,
      });

      if (data.priceRanges && data.priceRanges.length > 0) {
        const tiers = data.priceRanges.map((range, idx) => ({
          id: `tier-${idx}-${Date.now()}`,
          name: idx === 0 ? data.serviceId?.serviceName : `${range.minQty} - ${range.maxQty || "∞"} dịch vụ`,
          rangeName: range.rangeName || "",
          price: range.price || 0,
          quantity: range.quantity || 1,
          minQty: range.minQty,
          maxQty: range.maxQty,
        }));
        setEditableTiers(tiers);
        setOriginalTiers(JSON.parse(JSON.stringify(tiers)));
      } else {
        setEditableTiers([]);
        setOriginalTiers([]);
      }
    }
  }, [data]);

  const fetchHistory = useCallback(async (page = 1) => {
    try {
      setHistoryLoading(true);
      const response = await getAdjustmentHistory(token, data?._id, { page, limit: 10 });
      const newItems = response.data.map((item) => ({
        id: item._id,
        type: item.adjustmentType,
        amount: item.adjustmentAmount,
        reason: item.adjustmentContent,
        status: item.status,
      }));
      setAdjustmentHistory((prev) => (page === 1 ? newItems : [...prev, ...newItems]));
      setHistoryHasMore(page < response.pagination.totalPages);
      setHistoryPage(page);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  }, [getAdjustmentHistory, token, data?._id]);

  const fetchPendingAdjustment = useCallback(async () => {
    if (!getPendingAdjustment || !data?._id) {
      setPendingAdjustmentFromApi(null);
      setOriginalPendingAdjustmentFromApi(null);
      return;
    }

    try {
      const response = await getPendingAdjustment(token, data._id);
      const pendingData = response?.data || null;

      if (!pendingData) {
        setPendingAdjustmentFromApi(null);
        setOriginalPendingAdjustmentFromApi(null);
        return;
      }

      const normalizedPendingAdjustment = {
        id: pendingData._id,
        type: pendingData.adjustmentType,
        amount: pendingData.adjustmentAmount,
        reason: pendingData.adjustmentContent,
        status: pendingData.status,
      };

      setPendingAdjustmentFromApi(normalizedPendingAdjustment);
      setOriginalPendingAdjustmentFromApi({ ...normalizedPendingAdjustment });
    } catch (err) {
      console.error(err);
      setPendingAdjustmentFromApi(null);
      setOriginalPendingAdjustmentFromApi(null);
    }
  }, [getPendingAdjustment, token, data?._id]);

  useEffect(() => {
    if (isOpen && data?._id) {
      setAdjustmentHistory([]);
      setHistoryPage(1);
      setHistoryHasMore(true);
      setPendingAdjustment(null);
      setPendingDeleteIds([]);
      setHistoryDeleteIds([]);
      setShowDeleteConfirm(false);
      fetchHistory(1);
      fetchPendingAdjustment();
    }
  }, [isOpen, data?._id, fetchHistory, fetchPendingAdjustment]);

  const checkTierChanges = useCallback(() => {
    if (editableTiers.length !== originalTiers.length) return true;
    for (let i = 0; i < editableTiers.length; i++) {
      if (
        editableTiers[i].price !== originalTiers[i].price ||
        editableTiers[i].quantity !== originalTiers[i].quantity ||
        editableTiers[i].rangeName !== originalTiers[i].rangeName
      ) {
        return true;
      }
    }
    return false;
  }, [editableTiers, originalTiers]);

  const hasPendingAdjustmentFromApiChanges = useCallback(() => {
    if (!pendingAdjustmentFromApi || !originalPendingAdjustmentFromApi) return false;

    return (
      (pendingAdjustmentFromApi.reason || "") !== (originalPendingAdjustmentFromApi.reason || "") ||
      (pendingAdjustmentFromApi.type || "") !== (originalPendingAdjustmentFromApi.type || "") ||
      Number(pendingAdjustmentFromApi.amount || 0) !== Number(originalPendingAdjustmentFromApi.amount || 0)
    );
  }, [pendingAdjustmentFromApi, originalPendingAdjustmentFromApi]);

  const hasEstimateChanges = useCallback(() => {
    const currentStatus = form.status?.value || null;
    const originalStatus = data?.status || null;
    const currentVatPercent = Number(form.vatPercent || 0);
    const originalVatPercent = Number(data?.vatPercent || 0);
    const currentQuantity = Number(form.quantity || 1);
    const originalQuantity = Number(data?.quantity || 1);

    if (currentStatus !== originalStatus) return true;
    if (currentVatPercent !== originalVatPercent) return true;
    if (editableTiers.length > 0) return checkTierChanges();

    return currentQuantity !== originalQuantity;
  }, [checkTierChanges, data?.quantity, data?.status, data?.vatPercent, editableTiers.length, form.quantity, form.status?.value, form.vatPercent]);

  const calculateTotals = ({ includePendingAdjustment = true } = {}) => {
    const vatPercent = Number(form.vatPercent || 0);
    const hasTiers = editableTiers.length > 0;

    let B = 0;
    if (hasTiers) {
      B = editableTiers.reduce((total, t) => total + (Number(t.price) || 0) * (Number(t.quantity) || 1), 0);
    } else {
      B = Number(data.unitPrice || 0) * Number(form.quantity || 1);
    }

    const A = Number(data.totalAmount || 0);
    const hasServiceChanges = checkTierChanges() || (!hasTiers && Number(form.quantity || 1) !== Number(data.quantity || 1));
    const baseSubtotal = hasServiceChanges ? B : Math.round(A / (1 + (data.vatPercent || 0) / 100));

    let finalSubtotal = baseSubtotal;
    const previewAdjustment = includePendingAdjustment
      ? pendingAdjustment
        ? { type: pendingAdjustment.type, amount: pendingAdjustment.adjustmentAmount }
        : pendingAdjustmentFromApi
          ? { type: pendingAdjustmentFromApi.type, amount: pendingAdjustmentFromApi.amount }
          : null
      : null;

    if (previewAdjustment && Number(previewAdjustment.amount || 0) > 0) {
      const adjAmount = Number(previewAdjustment.amount);
      if (previewAdjustment.type === "Tăng") finalSubtotal += adjAmount;
      else if (previewAdjustment.type === "Giảm") finalSubtotal -= adjAmount;
    }

    const vatAmount = Math.round(finalSubtotal * (vatPercent / 100));
    return { subtotal: finalSubtotal, total: finalSubtotal + vatAmount };
  };

  const handleUpdateTier = (idx, updated) => {
    setEditableTiers((prev) => prev.map((t, i) => (i === idx ? updated : t)));
  };

  const handleAddAdjustment = () => {
    if (pendingAdjustment || pendingAdjustmentFromApi) return;
    setPendingAdjustment({ id: Date.now(), type: "Tăng", adjustmentAmount: 0, reason: "" });
    const choDuyetStatus = ESTIMATE_STATUS_OPTIONS.find((opt) => opt?.value === "Chờ duyệt");
    if (choDuyetStatus) setForm((prev) => ({ ...prev, status: choDuyetStatus }));
  };

  const handleStagePendingAdjustmentDelete = () => {
    const adjustmentId = pendingAdjustmentFromApi?.id;
    if (!adjustmentId) return;

    setPendingDeleteIds((prev) =>
      prev.includes(adjustmentId) ? prev : [...prev, adjustmentId],
    );
    setPendingAdjustmentFromApi(null);
    setOriginalPendingAdjustmentFromApi(null);
  };

  const handleStageAdjustmentHistoryDelete = (adjustmentId) => {
    if (!adjustmentId) return;

    setHistoryDeleteIds((prev) =>
      prev.includes(adjustmentId) ? prev : [...prev, adjustmentId],
    );
    setAdjustmentHistory((prev) =>
      prev.filter((item) => item.id !== adjustmentId),
    );
  };

  const saveEstimate = async () => {
    const hasTiers = editableTiers.length > 0;
    const shouldUpdateEstimate = hasEstimateChanges() || !!pendingAdjustment;
    const shouldUpdatePendingAdjustment = hasPendingAdjustmentFromApiChanges();
    const hasDeletions =
      pendingDeleteIds.length > 0 || historyDeleteIds.length > 0;

    if (
      !shouldUpdateEstimate &&
      !shouldUpdatePendingAdjustment &&
      !hasDeletions
    ) {
      toast.info("Không có thay đổi để cập nhật");
      return;
    }

    const payload = {
      status: form.status?.value,
      vatPercent: form.vatPercent,
    };

    if (hasTiers && checkTierChanges()) {
      payload.priceRanges = editableTiers.map((t) => ({
        minQty: t.minQty || 1,
        maxQty: t.maxQty || null,
        price: Number(t.price) || 0,
        quantity: Number(t.quantity) || 1,
        rangeName: t.rangeName || "",
      }));
    } else if (!hasTiers) {
      payload.quantity = form.quantity;
    }

    if (pendingAdjustment) {
      const amt = Math.abs(Number(pendingAdjustment.adjustmentAmount));
      if (!pendingAdjustment.type || !pendingAdjustment.reason?.trim() || amt <= 0) {
        toast.warning("Vui lòng nhập đầy đủ thông tin điều chỉnh");
        return;
      }
      payload.status = "Chờ duyệt";
      payload.adjustmentContent = pendingAdjustment.reason || "";
      payload.adjustmentType = pendingAdjustment.type;
      payload.adjustmentAmount = amt;
    }

    try {
      setSaving(true);
      if (hasDeletions) {
        await Promise.all([
          ...pendingDeleteIds.map((adjustmentId) =>
            deletePendingAdjustment(token, data._id, adjustmentId),
          ),
          ...historyDeleteIds.map((adjustmentId) =>
            deleteAdjustmentHistory(token, data._id, adjustmentId),
          ),
        ]);
      }

      const tasks = [];

      if (shouldUpdateEstimate) {
        tasks.push(onUpdate(data._id, payload));
      }

      if (shouldUpdatePendingAdjustment) {
        const amt = Math.abs(Number(pendingAdjustmentFromApi?.amount));
        if (!pendingAdjustmentFromApi?.type || !pendingAdjustmentFromApi?.reason?.trim() || amt <= 0) {
          toast.warning("Vui lòng nhập đầy đủ thông tin điều chỉnh");
          return;
        }

        tasks.push(updatePendingAdjustment(token, data._id, {
          adjustmentContent: pendingAdjustmentFromApi.reason || "",
          adjustmentType: pendingAdjustmentFromApi.type,
          adjustmentAmount: amt,
        }));
      }

      await Promise.all(tasks);

      if (hasDeletions) {
        toast.success("Xoá điều chỉnh giá thành công");
        setPendingDeleteIds([]);
        setHistoryDeleteIds([]);
        setShowDeleteConfirm(false);
        await onAdjustmentDeleted?.();
      }

      if (shouldUpdatePendingAdjustment) {
        toast.success("Cập nhật điều chỉnh chờ duyệt thành công");
      }

      if (
        shouldUpdateEstimate ||
        shouldUpdatePendingAdjustment ||
        hasDeletions
      ) {
        setPendingAdjustment(null);
        fetchHistory(1);
        fetchPendingAdjustment();
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Cập nhật báo phí thất bại");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    const hasDeletions =
      pendingDeleteIds.length > 0 || historyDeleteIds.length > 0;

    if (hasDeletions) {
      setShowDeleteConfirm(true);
      return;
    }

    saveEstimate();
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const totals = calculateTotals({ includePendingAdjustment: false });
      const vatPercent = Number(form.vatPercent || 0);
      const tiers = editableTiers.length
        ? editableTiers
        : [
            {
              name: data.serviceId?.serviceName || "Dịch vụ",
              price: data.unitPrice,
              quantity: form.quantity,
            },
          ];
      const services = tiers.map((tier, index) => ({
        name:
          tier.rangeName ||
          tier.name ||
          data.serviceId?.serviceName ||
          "Dịch vụ",
        price: Number(tier.price ?? data.unitPrice) || 0,
        licenseFee:
          Number(
            tier.licenseFee ||
              tier.certificateFee ||
              tier.issueFee ||
              data.licenseFee ||
              data.certificateFee,
          ) || 0,
        quantity: Number(tier.quantity ?? form.quantity) || 1,
        vat: vatPercent,
        isChild: index > 0,
      }));
      const vatTotal = Math.max(0, totals.total - totals.subtotal);
      let adjustmentsForPdf = adjustmentHistory;

      try {
        const historyResponse = await getAdjustmentHistory(token, data?._id, {
          page: 1,
          limit: 1000,
        });
        adjustmentsForPdf = (historyResponse?.data || []).map((item) => ({
          id: item._id,
          type: item.adjustmentType,
          amount: item.adjustmentAmount,
          reason: item.adjustmentContent,
          status: item.status,
        }));
      } catch (historyError) {
        console.error("Không thể tải đầy đủ lịch sử điều chỉnh để in PDF:", historyError);
      }

      const approvedAdjustments = adjustmentsForPdf.filter((adjustment) => {
        const normalizedStatus = String(adjustment?.status || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .trim();

        const isPending =
          normalizedStatus.includes("cho duyet") ||
          normalizedStatus.includes("pending");

        return (
          !isPending &&
          !historyDeleteIds.includes(adjustment?.id) &&
          adjustment?.reason?.trim()
        );
      });
      const bodyHTML = buildQuotationBodyHTML({
        date: new Date().toLocaleDateString("vi-VN"),
        specialist: profileInfo?.specialist || "",
        email: profileInfo?.email || "",
        phone: profileInfo?.phone || "",
        services,
        adjustments: approvedAdjustments,
        subtotal: totals.subtotal,
        vatTotal,
        grandTotal: totals.total,
      });
      const [regularFontBase64, boldFontBase64, watermarkBase64] =
        await Promise.all([
          toBase64FromPublic("/fonts/NotoSerif-Regular.ttf"),
          toBase64FromPublic("/fonts/NotoSerif-Bold.ttf"),
          toBase64FromPublic("/images/watermark-45.png"),
        ]);
      const finalHtml = buildQuotationPrintHTML(bodyHTML, {
        regularFontBase64,
        boldFontBase64,
      });
      const res = await fetch(`${getBackendUrl()}/pdf/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          html: finalHtml,
          profileName: `Bao_phi_${profileInfo?.brand || "ho_so"}`,
          watermarkBase64,
          watermarkOpacity: 0.12,
          options: {
            width: PAGE_W,
            height: PAGE_H,
            deviceScaleFactor: 2,
          },
          watermarkMode: "fixed",
        }),
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bao_phi_${profileInfo?.brand || "ho_so"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Tải báo phí thành công");
    } catch (err) {
      toast.error("Không thể tải báo phí");
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-lg px-6 py-4 flex items-center gap-3 shadow-xl">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="font-medium text-gray-700">Đang tải báo phí...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <EstimatePopupForm
        isOpen={isOpen}
        onClose={onClose}
        data={data}
        form={form}
        setForm={setForm}
        editableTiers={editableTiers}
        onUpdateTier={handleUpdateTier}
        adjustmentHistory={adjustmentHistory}
        pendingAdjustment={pendingAdjustment}
        pendingAdjustmentFromApi={pendingAdjustmentFromApi}
        originalPendingAdjustmentFromApi={originalPendingAdjustmentFromApi}
        onAddAdjustment={handleAddAdjustment}
        onUpdatePendingAdjustment={(f, v) => setPendingAdjustment((p) => ({ ...p, [f]: v }))}
        onUpdatePendingAdjustmentFromApi={(f, v) => setPendingAdjustmentFromApi((p) => (p ? { ...p, [f]: v } : p))}
        onRemovePendingAdjustment={() => setPendingAdjustment(null)}
        onDeletePendingAdjustment={handleStagePendingAdjustmentDelete}
        onDeleteAdjustmentHistory={handleStageAdjustmentHistoryDelete}
        historyLoading={historyLoading}
        historyHasMore={historyHasMore}
        onLoadMoreHistory={() => fetchHistory(historyPage + 1)}
        totals={calculateTotals()}
        isDownloading={isDownloading}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        canEditEstimateStatus={canEditEstimateStatus}
      />
      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={saveEstimate}
        loading={saving}
        title="Xác nhận cập nhật báo phí"
        message={`Bạn có chắc muốn xoá ${
          pendingDeleteIds.length + historyDeleteIds.length
        } điều chỉnh giá và lưu các thay đổi?`}
      />
    </>
  );
};

export default EstimatePopupContainer;
